import React, { useState } from "react";
import { Box, Typography, Stack, Chip, Menu, MenuItem } from "@mui/material";
import { ArrowDropDown as DropdownIcon } from "@mui/icons-material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { LeadThread, LeadStatus, STATUS_CONFIG } from "./leadApi";
import { todayStr } from "./leadCellUtils";
import { formatShortDate } from "../../utils/dateFormat";

interface Props {
  lead:           LeadThread;
  onStatusChange: (id: number, status: LeadStatus) => void;
}

/* ─── Sub-components ───────────────────────────────────────────────────── */
function LeadAge({ createdAt }: { createdAt: string }) {
  const ms    = Date.now() - new Date(createdAt).getTime();
  const hours = ms / 3_600_000;
  const mins  = Math.floor(ms / 60_000);
  const days  = Math.floor(hours / 24);

  // Single tone for age — the row's left accent already telegraphs urgency.
  let label: string;
  if (hours < 2)       label = mins < 60 ? `${mins}m ago` : `${Math.floor(hours)}h ago`;
  else if (hours < 24) label = `${Math.floor(hours)}h ago`;
  else                 label = `${days}d ago`;

  return (
    <Typography component="span" sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#64748b" }}>
      {label}
    </Typography>
  );
}

/* ── ResponseBadge ────────────────────────────────────────────────────
   Resolves the operational "contact state" for a lead row. The badge
   MUST never contradict the pipeline status:

     • status === "new"        → "Not contacted" (red) only when
                                  first_response_at is null.
     • status > "new"          → ALWAYS treated as contacted. If the
                                  backend has stamped first_response_at
                                  (the new auto-stamp on status change),
                                  show the precise duration. If for some
                                  reason it hasn't, fall back to the
                                  status label ("Contacted") so the row
                                  never reads "Not contacted" while the
                                  status says otherwise.
     • status === "lost"       → no operational state to show. */
function ResponseBadge({ lead }: { lead: LeadThread }) {
  const status      = lead.status;
  const isLost      = status === "lost";
  const isNew       = status === "new";
  const hasResponse = !!lead.first_response_at;

  if (isLost) {
    return (
      <Typography component="span" sx={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600 }}>
        Closed
      </Typography>
    );
  }

  // Truly uncontacted: no response stamp AND status is still "new".
  if (!hasResponse && isNew) {
    return (
      <Typography component="span" sx={{ fontSize: "0.7rem", color: "#dc2626", fontWeight: 600 }}>
        Not contacted
      </Typography>
    );
  }

  // Has a stamped first-response time → render the precise duration.
  if (hasResponse) {
    const mins = lead.response_delay_minutes ?? Math.floor(
      (new Date(lead.first_response_at!).getTime() - new Date(lead.created_at).getTime()) / 60_000,
    );
    const label = mins < 60 ? `${mins}m` : mins < 1440 ? `${Math.floor(mins / 60)}h` : `${Math.floor(mins / 1440)}d`;
    return (
      <Typography component="span" sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#16a34a" }}>
        Contacted in {label}
      </Typography>
    );
  }

  // Status has progressed past "new" but the backend didn't stamp
  // first_response_at (legacy rows updated before this fix; or a
  // status change that bypassed the service layer). Surface the
  // status itself rather than the contradictory "Not contacted".
  return (
    <Typography component="span" sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#16a34a" }}>
      Contacted
    </Typography>
  );
}

/* ─── Main component ───────────────────────────────────────────────────── */
export const LeadInfoCell = React.memo(function LeadInfoCell({ lead, onStatusChange }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const cfg = STATUS_CONFIG[lead.status];

  const createdLabel = (() => {
    const d = new Date(lead.created_at);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  })();

  return (
    <Stack spacing={0.55}>
      {/* Created date + time — outline clock icon, slate label */}
      {createdLabel && (
        <Stack direction="row" alignItems="center" gap={0.6}>
          <AccessTimeOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.7rem", color: "#475569", fontVariantNumeric: "tabular-nums" }}>
            {createdLabel}
          </Typography>
        </Stack>
      )}

      {/* Age · Response */}
      <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap">
        <LeadAge createdAt={lead.created_at} />
        <Typography component="span" sx={{ color: "#cbd5e1", fontSize: "0.65rem" }}>·</Typography>
        <ResponseBadge lead={lead} />
      </Stack>

      {/* Status chip — muted, status-tinted pill with a small dropdown caret */}
      <Box sx={{ display: "inline-flex" }} onClick={e => e.stopPropagation()}>
        <Chip
          size="small"
          label={cfg?.label}
          deleteIcon={<DropdownIcon sx={{ fontSize: "14px !important" }} />}
          onDelete={e => { e.stopPropagation(); setAnchor(e.currentTarget.parentElement as HTMLElement); }}
          onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}
          sx={{
            bgcolor: "#fff", color: cfg?.color,
            fontWeight: 600, fontSize: "0.68rem", height: 22,
            border: `1px solid ${cfg?.color}40`,
            borderRadius: "6px",
            cursor: "pointer",
            "& .MuiChip-label": { px: 0.9 },
            "& .MuiChip-deleteIcon": { color: cfg?.color, ml: 0.1, mr: 0.3 },
            "&:hover": { bgcolor: `${cfg?.color}0a`, borderColor: cfg?.color },
          }}
        />
      </Box>

      {/* Follow-up date */}
      {lead.follow_up_at && (
        <Stack direction="row" alignItems="center" gap={0.5}>
          {lead.follow_up_at < todayStr
            ? <WarningAmberOutlinedIcon sx={{ fontSize: 11, color: "#dc2626", flexShrink: 0 }} />
            : <EventOutlinedIcon       sx={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }} />
          }
          <Typography sx={{
            fontSize: "0.67rem", fontWeight: 600,
            color: lead.follow_up_at < todayStr ? "#dc2626" : "#64748b",
            fontVariantNumeric: "tabular-nums",
          }}>
            {formatShortDate(lead.follow_up_at)}
          </Typography>
        </Stack>
      )}

      {/* Status dropdown menu */}
      <Menu
        anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { borderRadius: "10px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", minWidth: 180 } } }}
      >
        <Box sx={{ px: 2, py: 0.8, borderBottom: "1px solid #f1f5f9" }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Change Status
          </Typography>
        </Box>
        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
          <MenuItem
            key={k}
            selected={lead.status === k}
            onClick={() => { setAnchor(null); onStatusChange(lead.id, k as LeadStatus); }}
            sx={{ fontSize: 13, fontWeight: lead.status === k ? 700 : 400, gap: 1.5 }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: v.color, flexShrink: 0 }} />
            {v.label}
            {lead.status === k && (
              <Typography sx={{ ml: "auto", fontSize: 10, color: "#94a3b8" }}>current</Typography>
            )}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
});
